import React, { Component, createRef } from 'react';
import { FormGroup, FormControl } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';

class FilterDropDown extends Component {
  
  constructor(props) {
    super(props);
    this.state = {
      selected: [],
      isInvalid: false,
      text: props.value || '',
    };
    
    // Create a ref to access the Typeahead instance
    this.typeahead = createRef();
    
    // Bind the methods to 'this'
    this.handleBlur = this.handleBlur.bind(this);
    this.handleTypeaheadChange = this.handleTypeaheadChange.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.selectItem = this.selectItem.bind(this);

  }

  // Lifecycle method to handle prop changes
  componentDidUpdate(prevProps) {
    if (this.props.value !== prevProps.value) {
      this.setText(this.props.value || '');
    }
    if (this.state.isInvalid !== this.props.isInvalid) {
      this.setState({ isInvalid: this.props.isInvalid });
    }
  }

  selectItem(item) {
    this.handleTypeaheadChange([item]);
    setTimeout(() => {
      if (this.typeahead.current) {
        this.typeahead.current.blur();
      }
    }, 500);
  }

  handleBlur() {
    const { allowFreeText } = this.props;
    
    // If no valid selection and free text is not allowed, clear the input
    if (!allowFreeText && this.state.selected.length === 0) {
      this.typeahead.current.clear();
      this.handleChange(this.state.selected); // triggers onSelect(null)
    } 
    // Add this case: when allowFreeText is true but no selection, pass the current text value
    else if (allowFreeText && this.state.selected.length === 0 && this.typeahead.current) {
      const currentText = this.typeahead.current.state.text;
      if (currentText && this.props.onSelect) {
        this.props.onSelect({
          id: null,
          label: currentText,
          site_id: null
        });
      }
    }
  }

  handleInputChange(text) {
    // If desired, call onSelect on every keystroke to pass "virtual" selection:
    if (this.props.onSelect) {
      this.props.onSelect({
        id: null,
        label: text,
      });
    }
  }

  /**
   * Fired when Typeahead attempts to change selection (via mouse, enter key, etc.)
   * We override to handle the "Create New" option differently.
   */
  handleTypeaheadChange(selectedItems) {
    // Ensure selectedItems is always an array and items have valid labels
    const validSelectedItems = (selectedItems || []).filter(item => 
      item && typeof item.label === 'string' && item.label.length > 0
    );

    if (
      validSelectedItems.length > 0 &&
      validSelectedItems[0].isCreateNew &&
      this.props.createControl && 
      this.props.createControl.showHandler
    ) {
      // The user clicked or hit enter on the "Create New" option.
      // We don't want to treat it as a normal selection.
      // Instead, we show the modal and do nothing else.
      this.props.createControl.showHandler();

      // Clear selection if you like, so the Typeahead remains blank afterward:
      this.setState({ selected: [] });
      this.typeahead.current?.clear();
      return;
    }

    // Otherwise proceed with normal selection
    this.handleChange(selectedItems);
  }

  /**
   * Our normal "handleChange" logic: store the selected item(s),
   * call parent onSelect, etc.
   */
  handleChange(selectedItems) {
    // Ensure we're working with a valid array
    const validItems = Array.isArray(selectedItems) ? selectedItems : [];
    // make sure all of the items have a valid id
    const validIds = validItems.every(item => item && item.id != null) ? validItems : [];
    this.setState({ selected: validIds });

    // Trigger the onSelect callback if provided
    if (this.props.onSelect) {
      const selected = validItems.length > 0 ? validItems[0] : null;
      this.props.onSelect(selected);
    }
  }

  // Add this new method
  setText(text) {
    if (this.typeahead.current) {
      this.typeahead.current.setState({ text });
      // Also trigger the input change handler to maintain consistency
      this.handleInputChange(text);
    }
  }

  render() {
    const {
      items = [],
      placeholder,
      allowNew,
      displayId,
      validationMessage,
      noMatchesMessage,
    } = this.props;

    // Add validation and safeguards when creating options
    const options = (items || []).map(item => {
      if (!item) return null;
      
      const name = String(item.name || '').trim();
      const id = String(item.id || '').trim();
      
      // Only create option if we have valid strings
      if (!name || !id) return null;
      
      return {
        id: id,
        label: displayId ? `${id}: ${name}` : name,
      };
    }).filter(Boolean); // Remove any null entries

    const dynamicOptions = [
      ...(this.props.createNewLabel ? [{
        id: 'create-new-option',
        label: String(this.props.createNewLabel || 'Create New').trim(),
        isCreateNew: true,
      }] : []),
      ...options,
    ];

    return (
      <FormGroup>
        <Typeahead
          ref={this.typeahead}
          id="filter-dropdown"
          variant="light"
          options={dynamicOptions}
          placeholder={placeholder}
          selected={this.state.selected || []}
          onChange={this.handleTypeaheadChange}
          onBlur={this.handleBlur}
          onInputChange={this.handleInputChange}
          emptyLabel={noMatchesMessage || 'No matches found'}
          allowNew={allowNew}
          newSelectionPrefix="Add: "
          labelKey="label"
          selectHintOnEnter
          highlightOnlyResult
          isInvalid={this.state.isInvalid}
          clearButton    // Add clear button to help with state management
          key={`typeahead-${dynamicOptions.length}`}  // Force re-render when options change
          /**
           * 2) Render "Create New" in green + attach an onClick that
           *    stops propagation and calls your modal toggle.
           */
          renderMenuItemChildren={(option, props, index) => {
            if (option.isCreateNew) {
              return (
                <div
                  style={{ 
                    color: 'var(--bs-success)', 
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (this.props.createControl && this.props.createControl.showHandler) {
                      this.props.createControl.showHandler();
                    } 
                  }}
                >
                  {option.label}
                </div>
              );
            }
            // Style standard items with dark text color
            return (
              <div style={{ color: '#000000 !important' }}>
                {option.label}
              </div>
            );
          }}
          filterBy={(option, props) => {
            // Always show the Create New option
            if (option.isCreateNew) return true;
            
            // Default filtering for other options
            const text = props.text.toLowerCase();
            return option.label.toLowerCase().includes(text);
          }}
        />
        
        {this.state.isInvalid && (
          <FormControl.Feedback
            type="invalid"
            style={{ display: this.state.isInvalid ? 'block' : 'none' }}
          >
            {validationMessage}
          </FormControl.Feedback>
        )}
      </FormGroup>
    );
  }
}

export default FilterDropDown;
